import Plus from "assets/img/svg/plus";
import SettingBoolean from "components/shared/SettingBoolean";
import StyledButton from "components/shared/StyledButton";
import { PortalType } from "contexts/portal.context";
import {
    INITIAL_ADVANCE_LETTERS_SETTING,
    OPENING_BALANCE_LETTERS_SETTING,
    resolveAdvanceAmountLetters,
    useCompanySettings,
} from "hooks/useCompanySettings";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AgreementTemplate } from "store/agreementsStore";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { EPortalType } from "store/types";

import DefaultSignerBlock from "./DefaultSignerBlock";
import {
    getTemplateSignerEnabled,
    getTemplateSignerText,
    parseCustomTemplateIds,
    signerEnabledKey,
    signerTextKey,
} from "./signerHeaderUtils";
import styles from "./style.module.scss";
import TemplatePickerModal from "./TemplatePickerModal";
import TemplateSignerBlock from "./TemplateSignerBlock";

const AgreementSettingsTab = () => {
    const [t] = useTranslation();
    const { getCompanySettings } = useCompanySettings();
    const { openingBalanceInLetters, initialAdvanceInLetters, initialAdvanceLettersStored } =
        resolveAdvanceAmountLetters(getCompanySettings);
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);

    const rawSettings = useAppSelector((state) => state.settings.companySettings?.settings ?? []);
    const agreementsTemplate = useAppSelector((state) => state.agreements.agreementsTemplate);
    const defaultAgreementTemplate = useAppSelector((state) => state.agreements.defaultAgreementTemplate);
    const allTemplates = [defaultAgreementTemplate, ...agreementsTemplate].filter(
        (tpl): tpl is AgreementTemplate => tpl !== null && tpl !== undefined,
    );

    // --- Global boolean settings ---
    const [companySttg, setCompanySttg] = useState({
        [OPENING_BALANCE_LETTERS_SETTING]: openingBalanceInLetters,
        [INITIAL_ADVANCE_LETTERS_SETTING]: initialAdvanceInLetters,
        show_rates_in_letters_on_agreements: getCompanySettings("show_rates_in_letters_on_agreements"),
        agreements_show_signatory_job_title: getCompanySettings("agreements_show_signatory_job_title"),
        agreements_show_signature_date: getCompanySettings("agreements_show_signature_date"),
    });

    const agreementSttg = [
        {
            name: OPENING_BALANCE_LETTERS_SETTING,
            title: t("ContractSettingsPreferences.ShowAdvanceAmountLetters.Title"),
            description: t("ContractSettingsPreferences.ShowAdvanceAmountLetters.Description"),
            value: companySttg[OPENING_BALANCE_LETTERS_SETTING],
            show: portalType === EPortalType.BOOK,
        },
        {
            name: INITIAL_ADVANCE_LETTERS_SETTING,
            title: t("ContractSettingsPreferences.ShowInitialAdvanceAmountLetters.Title"),
            description: t("ContractSettingsPreferences.ShowInitialAdvanceAmountLetters.Description"),
            value: companySttg[INITIAL_ADVANCE_LETTERS_SETTING],
            show: portalType === EPortalType.BOOK,
        },
        {
            name: "show_rates_in_letters_on_agreements",
            title: t("ContractSettingsPreferences.ShowRatesLetters.Title"),
            description: t("ContractSettingsPreferences.ShowRatesLetters.Description"),
            value: companySttg["show_rates_in_letters_on_agreements"],
            show: portalType === EPortalType.BOOK,
        },
        {
            name: "agreements_show_signatory_job_title",
            title: t("ContractSettingsPreferences.ShowSignatoryJobTitle.Title"),
            description: t("ContractSettingsPreferences.ShowSignatoryJobTitle.Description"),
            value: companySttg["agreements_show_signatory_job_title"],
        },
        {
            name: "agreements_show_signature_date",
            title: t("ContractSettingsPreferences.ShowSignatureDate.Title"),
            description: t("ContractSettingsPreferences.ShowSignatureDate.Description"),
            value: companySttg["agreements_show_signature_date"],
        },
    ];

    const handleChange = (value: boolean, settingsName: string) => {
        const updates = [{ settingsName, settingsValue: value ? "1" : "0" }];
        if (settingsName === OPENING_BALANCE_LETTERS_SETTING && !initialAdvanceLettersStored) {
            updates.push({
                settingsName: INITIAL_ADVANCE_LETTERS_SETTING,
                settingsValue: companySttg[INITIAL_ADVANCE_LETTERS_SETTING] ? "1" : "0",
            });
        }
        setCompanySttg((prev) => ({ ...prev, [settingsName]: value }));
        dispatch(putCompanySettings(updates));
    };

    // --- Master toggle ---
    const [signerHeaderTextEnabled, setSignerHeaderTextEnabled] = useState<boolean>(
        !!getCompanySettings("agreements_internal_signer_header_text_enabled"),
    );

    const handleToggleSignerHeaderText = (enabled: boolean) => {
        setSignerHeaderTextEnabled(enabled);
        dispatch(
            putCompanySettings([
                { settingsName: "agreements_internal_signer_header_text_enabled", settingsValue: enabled ? "1" : "0" },
            ]),
        );
    };

    // --- Default (global) signature text ---
    const rawSignerHeaderText = getCompanySettings("agreements_internal_signer_header_text");
    const [persistedGlobalText, setPersistedGlobalText] = useState<string>(
        typeof rawSignerHeaderText === "string" ? rawSignerHeaderText : "",
    );
    const [defaultSignatureEnabled, setDefaultSignatureEnabled] = useState<boolean>(
        !!getCompanySettings("agreements_internal_signer_header_text_enabled_default"),
    );
    const [defaultExpanded, setDefaultExpanded] = useState<boolean | undefined>(undefined);

    const handleToggleDefaultSignature = (enabled: boolean) => {
        setDefaultSignatureEnabled(enabled);
        if (!enabled) setDefaultExpanded(undefined);
        dispatch(
            putCompanySettings([
                {
                    settingsName: "agreements_internal_signer_header_text_enabled_default",
                    settingsValue: enabled ? "1" : "0",
                },
            ]),
        );
    };

    const handleSaveGlobalText = (value: string) => {
        setPersistedGlobalText(value);
        setDefaultExpanded(false);
        dispatch(
            putCompanySettings([{ settingsName: "agreements_internal_signer_header_text", settingsValue: value }]),
        );
    };

    // --- Per-template signer header overrides ---
    const [addedTemplateIds, setAddedTemplateIds] = useState<number[]>(() => parseCustomTemplateIds(rawSettings));
    const [templateEnabledMap, setTemplateEnabledMap] = useState<Record<number, boolean>>(() =>
        Object.fromEntries(
            parseCustomTemplateIds(rawSettings).map((id) => [id, getTemplateSignerEnabled(rawSettings, id)]),
        ),
    );
    const [expandedTemplateIds, setExpandedTemplateIds] = useState<Record<number, boolean>>({});
    const [modalOpen, setModalOpen] = useState(false);

    const remainingTemplates = allTemplates.filter((tpl) => !addedTemplateIds.includes(tpl.id));

    const handleAddTemplate = (templateId: number) => {
        const newIds = [...addedTemplateIds, templateId];
        setAddedTemplateIds(newIds);
        setTemplateEnabledMap((prev) => ({ ...prev, [templateId]: getTemplateSignerEnabled(rawSettings, templateId) }));
        dispatch(
            putCompanySettings([
                { settingsName: "agreements_signer_custom_template_ids", settingsValue: JSON.stringify(newIds) },
            ]),
        );
        setModalOpen(false);
    };

    const handleRemoveTemplate = (templateId: number) => {
        const newIds = addedTemplateIds.filter((id) => id !== templateId);
        setAddedTemplateIds(newIds);
        setTemplateEnabledMap((prev) => {
            const next = { ...prev };
            delete next[templateId];
            return next;
        });
        setExpandedTemplateIds((prev) => {
            const next = { ...prev };
            delete next[templateId];
            return next;
        });
        dispatch(
            putCompanySettings([
                { settingsName: "agreements_signer_custom_template_ids", settingsValue: JSON.stringify(newIds) },
                { settingsName: signerEnabledKey(templateId), settingsValue: "0" },
            ]),
        );
    };

    const handleToggleTemplate = (templateId: number, enabled: boolean) => {
        setTemplateEnabledMap((prev) => ({ ...prev, [templateId]: enabled }));
        if (!enabled) {
            setExpandedTemplateIds((prev) => {
                const next = { ...prev };
                delete next[templateId];
                return next;
            });
        }
        dispatch(
            putCompanySettings([{ settingsName: signerEnabledKey(templateId), settingsValue: enabled ? "1" : "0" }]),
        );
    };

    const handleSaveTemplateText = (templateId: number, value: string) => {
        setExpandedTemplateIds((prev) => ({ ...prev, [templateId]: false }));
        dispatch(putCompanySettings([{ settingsName: signerTextKey(templateId), settingsValue: value }]));
    };

    // Auto-disable toggles with no saved text when leaving the page
    const cleanupRef = useRef<() => void>();
    cleanupRef.current = () => {
        const actions: { settingsName: string; settingsValue: string }[] = [];
        if (defaultSignatureEnabled && !persistedGlobalText.trim())
            actions.push({
                settingsName: "agreements_internal_signer_header_text_enabled_default",
                settingsValue: "0",
            });
        for (const id of addedTemplateIds) {
            if (templateEnabledMap[id] && !getTemplateSignerText(rawSettings, id).trim()) {
                actions.push({ settingsName: signerEnabledKey(id), settingsValue: "0" });
            }
        }
        if (actions.length > 0) dispatch(putCompanySettings(actions));
    };
    useEffect(() => () => cleanupRef.current?.(), []);

    return (
        <div className={styles["agreement-settings-tab"]}>
            {agreementSttg.map((option) => {
                const { show = true } = option;
                if (show)
                    return (
                        <SettingBoolean
                            key={option.name}
                            name={option.name}
                            title={option.title}
                            description={option.description}
                            value={option.value}
                            handleChange={(value: boolean) => handleChange(value, option.name)}
                        />
                    );
            })}

            {/* Master toggle */}
            <SettingBoolean
                name="agreements_internal_signer_header_text_enabled"
                title={t("ContractSettingsPreferences.InternalSignerHeaderText.Title")}
                description={t("ContractSettingsPreferences.InternalSignerHeaderText.Description")}
                value={signerHeaderTextEnabled}
                handleChange={handleToggleSignerHeaderText}
            />

            {signerHeaderTextEnabled && (
                <div className={styles["template-signers"]}>
                    <DefaultSignerBlock
                        defaultSignatureEnabled={defaultSignatureEnabled}
                        persistedGlobalText={persistedGlobalText}
                        defaultExpanded={defaultExpanded}
                        onToggle={handleToggleDefaultSignature}
                        onSave={handleSaveGlobalText}
                        onSetExpanded={setDefaultExpanded}
                    />

                    {addedTemplateIds.map((templateId) => {
                        const template = allTemplates.find((tpl) => tpl.id === templateId);
                        if (!template) return null;
                        const isEnabled = templateEnabledMap[templateId] ?? false;
                        const persistedValue = getTemplateSignerText(rawSettings, templateId);
                        const hasSavedText = isEnabled && !!persistedValue.trim();
                        const isExpanded = expandedTemplateIds[templateId] ?? !hasSavedText;
                        return (
                            <TemplateSignerBlock
                                key={templateId}
                                template={template}
                                isEnabled={isEnabled}
                                isExpanded={isExpanded}
                                persistedValue={persistedValue}
                                onToggle={(enabled) => handleToggleTemplate(templateId, enabled)}
                                onSave={(value) => handleSaveTemplateText(templateId, value)}
                                onRemove={() => handleRemoveTemplate(templateId)}
                                onSetExpanded={(val) =>
                                    setExpandedTemplateIds((prev) => ({ ...prev, [templateId]: val }))
                                }
                            />
                        );
                    })}

                    {remainingTemplates.length > 0 && (
                        <StyledButton buttonType="outlined" onClick={() => setModalOpen(true)}>
                            <Plus />
                            {t("ContractSettingsPreferences.AddTemplate")}
                        </StyledButton>
                    )}

                    <TemplatePickerModal
                        open={modalOpen}
                        onClose={() => setModalOpen(false)}
                        remainingTemplates={remainingTemplates}
                        onAdd={handleAddTemplate}
                    />
                </div>
            )}
        </div>
    );
};

export default AgreementSettingsTab;
